import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import {
  resetWatchIntervals,
  saveWatchAlertTime,
  saveWatchIntervals,
  saveWatchServices,
  saveWatchSettings,
  watchMonitoringQuery,
  watchPageQuery,
  watchServiceTemplate,
  type DeviceWatchPage,
  type WatchInterval,
  type WatchMonitoring,
  type WatchOption,
  type WatchServiceRow,
} from '../api/devices'
import { ApiError } from '../api/client'
import { CheckField, FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { Forbidden } from '../ui/Forbidden'
import { LoadFailed } from '../ui/LoadFailed'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Watch - remote_monitoring/device_details/deviceSettings.php
 * (generalSettings + servicesSettings). Update posteaza aceleasi campuri ca
 * saveSnmpSettings / saveDeviceServices. Butoanele "Monitoring settings" - de
 * pe masina si de pe fiecare serviciu - deschid `MonitoringSettingsModal`
 * (deviceMonitoringSettingsModal.php / serviceMonitoringSettings.php): la
 * fel doua panouri, intervalele in care NU se monitorizeaza si ora de alerta.
 *
 * Instructiunile de instalare leaga acum FISIERUL, nu capul de PHP:
 * `Proactivitee::downloadProcedures` nu facea decat un force_download pe
 * `/download/Nsclient.zip` si pe PDF-ul de Linux, iar Apache serveste
 * amandoua direct (`RewriteCond %{REQUEST_FILENAME} !-f` inaintea lui
 * index.php). Cu modulul Proactivitee scos din CodeIgniter, un invelis de
 * PHP peste un fisier static n-avea ce sa mai apere.
 *
 * -----------------------------------------------------------------------------
 * ASEZAREA: 10 septembrie 2026, pe grila comuna
 * -----------------------------------------------------------------------------
 *
 * Formularul era `form-horizontal labelcustomize`: eticheta pe `col-md-3` la
 * stanga, campul pe `col-md-9` la dreapta, si o coloana de `col-md-2` lipita in
 * dreapta cu cele sapte bifari. Acum trece prin `ui/FormGrid`, ca fisa contului
 * si My Account: eticheta DEASUPRA campului, doua campuri pe rand, bifarile pe
 * aceleasi randuri, butoanele in `FormActions`.
 *
 * Masurat in Chromium la 1600px, cu foile adevarate servite pe HTTP si panoul
 * desenat in randul desfasurat al listei:
 *
 *   - „Agent Location" avea 112px de camp intr-o coloana de 810px, „Agent IP
 *     Address" 186px. Acum 745px, cat jumatatea de rand. Pricina e cea scrisa
 *     in `FormGrid`: invelisul listei poarta `form-inline`, iar
 *     `.form-inline .form-control { width: auto }` lasa campul cat il face
 *     browserul. `od-form-row` din `FormRow` o scoate din joc;
 *   - bifa „Autoclose" masura 50x30px si era un dreptunghi alb gol, in care nu
 *     se vedea daca e pornita. Vezi `FLAGS` mai jos pentru de ce;
 *   - bifa „Active" a unui serviciu masura 13x34px - `form-control` pe o casuta.
 *     Amandoua sunt acum casuta obisnuita de 13x13, cu eticheta langa ea.
 *
 * `form-horizontal` a plecat din amandoua formularele fiindca aliniaza
 * `.control-label` la DREAPTA de la 768px in sus, adica se bate cap in cap cu
 * eticheta de deasupra campului. `labelcustomize` ramane: e carlig de plugin
 * din portal, nicio foaie n-are nimic pe el.
 *
 * CE SE TRIMITE nu s-a atins: `onSaveSettings` si `onSaveServices` string cele
 * doua pachete din aceeasi stare, cu aceleasi nume si in aceeasi ordine.
 */

type WatchFlags = {
  autoclose: boolean
  escalate: boolean
  gateway: boolean
  hypervisor: boolean
  no_keepalive: boolean
  maintenance: boolean
  development: boolean
}

type ServiceDraft = {
  key: number
  service_id: string
  monitored: boolean
  values: { field: string; value: string; placeholder?: string }[]
}

/**
 * Trei stari inaintea formularului, in ordinea asta: refuzul (403) e ecranul
 * de fara drept; o citire picata spune CE a picat si da Retry, in loc de
 * "Something went wrong" cu un numar de referinta; abia apoi spinnerul. O
 * masina fara setari SNMP nu e o eroare - `settings` vine `null` si
 * formularul se deschide gol, cu o nota care spune ca nu se urmareste inca.
 */
export function DeviceWatch({ id }: ScreenProps) {
  const page = useQuery(watchPageQuery(id))
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error) return <LoadFailed error={page.error} what="What we watch" onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />
  return <WatchForm id={id} page={page.data} />
}

function WatchForm({ id, page }: { id: number; page: DeviceWatchPage }) {
  const query = useQueryClient()
  const settings = page.settings
  const [server, setServer] = useState(settings?.server_id ? String(settings.server_id) : '')
  const [community, setCommunity] = useState(settings?.community_id ? String(settings.community_id) : '')
  const [parent, setParent] = useState(settings?.parent_id ? String(settings.parent_id) : '')
  const [hypervisors, setHypervisors] = useState(page.device_hypervisors.map(String))
  const [redundancy, setRedundancy] = useState(page.device_redundancy.map(String))
  const [severity, setSeverity] = useState(settings?.severity ? String(settings.severity) : '')
  const [agentIp, setAgentIp] = useState(settings?.agent_ip ?? '')
  const [udpPort, setUdpPort] = useState(settings?.udp_port ?? '')
  const [systemType, setSystemType] = useState(page.device.so === 'windows-server' ? '1' : page.device.so === 'linux-server' ? '2' : '')
  const [coreApps, setCoreApps] = useState(page.device_core_apps.map(String))
  const [flags, setFlags] = useState<WatchFlags>({
    autoclose: settings?.autoclose === 1,
    escalate: settings?.escalate === 1,
    gateway: settings?.gateway === 1,
    hypervisor: settings?.hypervisor === 1,
    no_keepalive: settings?.no_keepalive === 1,
    maintenance: settings?.maintenance === 1,
    development: settings?.development === 1,
  })
  const [services, setServices] = useState<ServiceDraft[]>(() => seedServices(page))
  /* null = inchis; 0 = modalul masinii; >0 = modalul serviciului. */
  const [monitoringFor, setMonitoringFor] = useState<number | null>(null)

  async function onSaveSettings(event: FormEvent) {
    event.preventDefault()
    const fields: Record<string, string | number | Array<string | number>> = {
      device: id,
      server,
      community,
      parent,
      severity,
      agent_ip: agentIp,
      udp_port: udpPort,
      hypervisors,
      redundancy,
      core_apps: coreApps,
    }
    if (page.device.tip === 3) fields.system_type = systemType
    for (const [key, on] of Object.entries(flags)) {
      if (on) fields[key] = '1'
    }
    try {
      await saveWatchSettings(id, fields)
      toast('Settings saved', 'Success')
      await query.invalidateQueries({ queryKey: ['device', id, 'watch'] })
    } catch (error) {
      toast(error instanceof Error ? error.message : 'An error occured! Please try again..', 'Error')
    }
  }

  async function onSaveServices(event: FormEvent) {
    event.preventDefault()
    const fields: Record<string, string | number> = { device: id }
    for (const row of services) {
      fields[`service[${row.key}]`] = row.service_id
      if (row.monitored) fields[`monitored[${row.key}]`] = '1'
      if (row.values.length === 0) {
        fields[`values[${row.key}][Port]`] = ''
      } else {
        for (const value of row.values) {
          fields[`values[${row.key}][${value.field}]`] = value.value
        }
      }
    }
    try {
      await saveWatchServices(id, fields)
      toast('Services saved', 'Success')
      await query.invalidateQueries({ queryKey: ['device', id, 'watch'] })
    } catch (error) {
      toast(error instanceof Error ? error.message : 'An error occured! Please try again..', 'Error')
    }
  }

  return (
    <div className="row">
      <div className="btn-group pull-right">
        <button aria-expanded="false" aria-haspopup="true" data-toggle="dropdown" className="btn btn-sm btn-primary dropdown-toggle" type="button">
          <i className="fa fa-download"></i> Download Instructions <span className="caret"></span>
        </button>
        <ul className="dropdown-menu arrow pull-right">
          <li>
            <a href="/download/Nsclient.zip" download>
              <i className="fab fa-windows"></i> Windows
            </a>
          </li>
          <li>
            <a href="/download/SNMP_and_NRPE_Installing_Procedure_on_Linux.pdf" download>
              <i className="fa fa-linux"></i> Linux
            </a>
          </li>
        </ul>
      </div>

      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>General</strong> settings
        </h4>
      </header>
      <div className="panel-body">
        {!settings && (
          <div className="alert alert-info od-watch-empty" role="status">
            Nothing is watched from outside on this device yet. Fill in the settings below and press{' '}
            <strong>Update</strong> to start.
          </div>
        )}
        <form className="labelcustomize saveSnmpSettings" action="/proactivitee/saveSnmpSettings" method="post" onSubmit={onSaveSettings}>
          <input type="hidden" name="device" value={id} />
          {pairs([
            <SelectField key="server" label="Agent Location" id="watch_server" name="server" required value={server} onChange={setServer}>
              <option value="">Select...</option>
              {page.servers.map((row) => (
                <option key={row.ID} value={row.ID}>
                  {row.name}
                </option>
              ))}
            </SelectField>,
            <SelectField key="community" label="SNMP Community" id="watch_community" name="community" value={community} onChange={setCommunity}>
              <option value="">Select...</option>
              {page.communities.map((row) => (
                <option key={row.ID} value={row.ID}>
                  {row.name}
                </option>
              ))}
            </SelectField>,
            <SelectField key="parent" label="Parent" id="watch_parent" name="parent" value={parent} onChange={setParent}>
              <option value="">Select...</option>
              {page.gateway_devices.map((row) => (
                <option key={row.ID} value={row.ID}>
                  {row.device_name}
                </option>
              ))}
            </SelectField>,
            page.device.virtual === 1 ? (
              page.hypervisors.length > 0 ? (
                <MultiField
                  key="hosts"
                  label="Hosts"
                  id="watch_hypervisors"
                  name="hypervisors[]"
                  value={hypervisors}
                  onChange={setHypervisors}
                  options={page.hypervisors}
                  text={(row) => row.device_name ?? ''}
                />
              ) : (
                <HintField key="hosts" label="Hosts" text="No hypervisors defined!" />
              )
            ) : null,
            page.monitored_devices.length > 0 ? (
              <MultiField
                key="redundancy"
                label="Redundancy"
                id="watch_redundancy"
                name="redundancy[]"
                value={redundancy}
                onChange={setRedundancy}
                options={page.monitored_devices}
                text={(row) => row.Denumire ?? ''}
                liveSearch
              />
            ) : (
              <HintField key="redundancy" label="Redundancy" text="No monitored devices!" />
            ),
            <SelectField key="severity" label="Severity" id="watch_severity" name="severity" value={severity} onChange={setSeverity}>
              <option value="">Select...</option>
              <option value="1">No impact</option>
              <option value="2">Affected</option>
              <option value="3">Critical</option>
            </SelectField>,
            <HalfField key="agent_ip" label="Agent IP Address" required htmlFor="watch_agent_ip">
              <input
                id="watch_agent_ip"
                type="text"
                name="agent_ip"
                value={agentIp}
                onChange={(e) => setAgentIp(e.target.value)}
                className="form-control"
                required
              />
            </HalfField>,
            <HalfField key="udp_port" label="SNMP UDP Port" htmlFor="watch_udp_port">
              <input
                id="watch_udp_port"
                type="text"
                name="udp_port"
                value={udpPort === '' ? (settings ? ' ' : '') : udpPort}
                onChange={(e) => setUdpPort(e.target.value.trim())}
                className="form-control"
              />
            </HalfField>,
            page.device.tip === 3 ? (
              <SelectField key="system_type" label="System Type" id="watch_system_type" name="system_type" required value={systemType} onChange={setSystemType}>
                <option value="">Select...</option>
                <option value="1">windows-server</option>
                <option value="2">linux-server</option>
              </SelectField>
            ) : null,
            <MultiField
              key="core_apps"
              label="Core apps"
              id="watch_core_apps"
              name="core_apps[]"
              value={coreApps}
              onChange={setCoreApps}
              options={page.core_apps}
              text={(row) => (page.core_apps_from_sp ? `${row.company_name} - ${row.application_name}` : (row.application_name ?? ''))}
              liveSearch
            />,
          ])}

          {pairs(
            FLAGS.map(([name, label, hook]) => (
              <CheckField
                key={name}
                id={`watch_${name}`}
                label={label}
                className={hook}
                checked={flags[name]}
                onChange={(v) => setFlags({ ...flags, [name]: v })}
              />
            )),
          )}

          <FormActions>
            <button className="btn btn-theme saveSettings" type="submit">
              Update
            </button>{' '}
            <button type="button" className="btn btn-sm btn-primary device-intervals" onClick={() => setMonitoringFor(0)}>
              <i className="fas fa-pencil-alt"></i> Monitoring settings
            </button>
          </FormActions>
        </form>
      </div>

      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Services</strong> settings
        </h4>
      </header>
      <div className="panel-body">
        {page.device.tip === 3 && page.device.os_type === 0 ? (
          <div className="alert alert-danger" role="alert">
            <p>System type is not defined! In order to add services, you must set first, the system type.</p>
          </div>
        ) : (
          <form className="labelcustomize saveDeviceServices" action="/proactivitee/saveDeviceServices" method="post" onSubmit={onSaveServices}>
            <input type="hidden" name="device" value={id} />
            {services.map((row) => (
              <ServiceRow
                key={row.key}
                row={row}
                list={page.services_list}
                onChange={(next) => setServices((cur) => cur.map((s) => (s.key === row.key ? next : s)))}
                onRemove={() =>
                  setServices((cur) =>
                    cur.length === 1
                      ? [
                          {
                            ...cur[0],
                            service_id: '',
                            values: [{ field: 'Port', value: '' }],
                            monitored: false,
                          },
                        ]
                      : cur.filter((s) => s.key !== row.key),
                  )
                }
                onMonitoring={() => setMonitoringFor(Number(row.service_id))}
                onPickService={async (serviceId) => {
                  const fields = serviceId ? await watchServiceTemplate(id, Number(serviceId)) : []
                  setServices((cur) =>
                    cur.map((s) =>
                      s.key === row.key
                        ? {
                            ...s,
                            service_id: serviceId,
                            values:
                              fields.length > 0
                                ? fields.map((f) => ({
                                    field: f.field,
                                    value: f.value,
                                    placeholder: f.placeholder,
                                  }))
                                : [{ field: 'Port', value: '' }],
                          }
                        : s,
                    ),
                  )
                }}
              />
            ))}
            <FormActions>
              <button
                type="button"
                className="btn btn-sm btn-primary add-service"
                onClick={() =>
                  setServices((cur) => {
                    const last = cur[cur.length - 1]
                    return [
                      ...cur,
                      {
                        key: (last?.key ?? 0) + 1,
                        service_id: '',
                        monitored: false,
                        values: [{ field: 'Port', value: '' }],
                      },
                    ]
                  })
                }
              >
                <i className="fas fa-plus"></i> Add service
              </button>{' '}
              <button className="btn btn-sm btn-danger" type="submit">
                Update services
              </button>
            </FormActions>
          </form>
        )}
      </div>
      {monitoringFor !== null ? (
        <MonitoringSettingsModal id={id} service={monitoringFor > 0 ? monitoringFor : null} onClose={() => setMonitoringFor(null)} />
      ) : null}
    </div>
  )
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const ALERT_MINUTES = [15, 30, 45, 60, 90, 120]

type IntervalDraft = {
  key: number
  day: string
  start_time: string
  end_time: string
}

function seedIntervals(rows: WatchInterval[]): IntervalDraft[] {
  if (rows.length === 0) return [{ key: 1, day: '', start_time: '', end_time: '' }]
  return rows.map((r, i) => ({
    key: i + 1,
    day: String(DAYS.indexOf(r.day) + 1 || ''),
    start_time: r.start_time,
    end_time: r.end_time,
  }))
}

/**
 * "Edit monitoring settings" - deviceMonitoringSettingsModal.php cand
 * `service` e null, serviceMonitoringSettings.php altfel. Doua panouri:
 *
 *  - Monitoring intervals: randuri Day / Start time / End time, cu + si x;
 *    Save posteaza day[] / start_time[] / end_time[], Reset le sterge pe toate.
 *    Ferestrele sunt cele in care NU se monitorizeaza - ca sa scoti 01:00-03:00
 *    pui Start 03:01, End 00:59 (tooltip-ul vechi).
 *  - Alerts intervals: Notify time, dupa cate minute de necaz se da alarma.
 *
 * Ceasul e `<input type="time">` in loc de datetimepicker-ul din jQuery:
 * aceeasi valoare hh:mm, fara plugin.
 */
export function MonitoringSettingsModal({ id, service, onClose }: { id: number; service: number | null; onClose: () => void }) {
  const current = useQuery(watchMonitoringQuery(id, service))
  const modalId = service ? 'serviceMonitoringSettings' : 'deviceMonitoringSettings'
  return (
    <div className="modal fade in" id={modalId} tabIndex={-1} role="dialog" aria-labelledby="myModalLabel" style={{ display: 'block' }}>
      <div className="modal-dialog" role="document">
        <div className="modal-content modal-lg">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 className="modal-title" id="myModalLabel">
              Edit monitoring settings
            </h4>
          </div>
          {/* Formularul se naste abia cu datele in mana: starea lui porneste din ele. */}
          {current.data ? (
            <MonitoringForm id={id} service={service} initial={current.data} onClose={onClose} />
          ) : (
            <div className="modal-body">
              <Spinner />
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function MonitoringForm({ id, service, initial, onClose }: { id: number; service: number | null; initial: WatchMonitoring; onClose: () => void }) {
  const query = useQueryClient()
  const [drafts, setRows] = useState<IntervalDraft[]>(() => seedIntervals(initial.intervals))
  const [alertTime, setAlertTime] = useState<string>(initial.alert_time ? String(initial.alert_time) : '')
  const formHook = service ? 'serviceIntervals' : 'deviceIntervals'
  const alertHook = service ? 'serviceAlertTime' : 'deviceAlertTime'

  async function done(next: Promise<unknown>) {
    try {
      await next
      toast('Changes saved', 'Success')
      await query.invalidateQueries({ queryKey: ['device', id, 'watch'] })
      onClose()
    } catch (error) {
      toast(error instanceof Error ? error.message : 'An error occured! Please try again..', 'Error')
    }
  }

  function saveIntervals(event: FormEvent) {
    event.preventDefault()
    void done(
      saveWatchIntervals(
        id,
        service,
        drafts.map((r) => ({
          day: r.day,
          start_time: r.start_time,
          end_time: r.end_time,
        })),
      ),
    )
  }

  function saveAlert(event: FormEvent) {
    event.preventDefault()
    void done(saveWatchAlertTime(id, service, alertTime))
  }

  function patch(key: number, part: Partial<IntervalDraft>) {
    setRows((cur) => cur.map((r) => (r.key === key ? { ...r, ...part } : r)))
  }

  function remove(key: number) {
    setRows((cur) => {
      if (cur.length === 1) return [{ key: cur[0]!.key, day: '', start_time: '', end_time: '' }]
      return cur.filter((r) => r.key !== key)
    })
  }

  function add() {
    setRows((cur) => {
      const last = cur[cur.length - 1]
      return [...cur, { key: (last?.key ?? 0) + 1, day: '', start_time: '', end_time: '' }]
    })
  }

  return (
    <div className="modal-body">
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Monitoring</strong> intervals{' '}
          <i
            className="fas fa-info-circle color-blue"
            title="This is used when you want to exclude a service from monitoring to a specified time frame. Eg: if you want to exclude the service from 01:00 to 03:00 you have to put the following settings: [Start time: 03:01 - End time: 00:59]."
          ></i>
        </h4>
      </header>
      <div className="panel-body">
        {
          <form className={`form-horizontal labelcustomize ${formHook}`} method="post" onSubmit={saveIntervals}>
            <input type="hidden" name="device" value={id} />
            {service ? <input type="hidden" name="service" value={service} /> : null}
            {drafts.map((row) => (
              <div key={row.key} className="col-md-11 interval">
                <div className="form-group col-md-3 pull-left">
                  <div className="input-group selectContainer">
                    <span className="input-group-addon">Day</span>
                    <select name="day[]" className="form-control" required value={row.day} onChange={(e) => patch(row.key, { day: e.target.value })}>
                      <option value="">Select...</option>
                      {DAYS.map((d, i) => (
                        <option key={d} value={String(i + 1)}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group col-md-4 pull-left">
                  <div className="input-group inputGroupContainer">
                    <span className="input-group-addon">Start time</span>
                    <input
                      type="time"
                      className="form-control timepicker"
                      name="start_time[]"
                      value={row.start_time}
                      required
                      onChange={(e) => patch(row.key, { start_time: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group col-md-4 pull-left">
                  <div className="input-group inputGroupContainer">
                    <span className="input-group-addon">End time</span>
                    <input
                      type="time"
                      className="form-control timepicker"
                      name="end_time[]"
                      value={row.end_time}
                      required
                      onChange={(e) => patch(row.key, { end_time: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group col-md-1 pull-left">
                  <button type="button" className="btn btn-sm btn-danger remove-interval" onClick={() => remove(row.key)}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            ))}
            <div className="col-md-1">
              <button type="button" className="btn btn-sm btn-primary add-interval" onClick={add}>
                <i className="fas fa-plus"></i>
              </button>
            </div>
            <div className="col-md-12">
              <button type="submit" className="btn btn-sm btn-primary saveIntervals">
                Save
              </button>{' '}
              <button
                type="button"
                className="btn btn-sm btn-danger resetIntervals"
                data-device={id}
                data-service={service ?? undefined}
                onClick={() => void done(resetWatchIntervals(id, service))}
              >
                Reset intervals
              </button>
            </div>
          </form>
        }
      </div>

      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Alerts</strong> intervals{' '}
          <i
            className="fas fa-info-circle color-blue"
            title="This represents the time in  minutes when an alert will be generated. Eg: if you want to receive an alert only after 30 min of high load, set [Notify time: 30 minutes]."
          ></i>
        </h4>
      </header>
      <div className="panel-body">
        {
          <form className={`form-horizontal labelcustomize ${alertHook}`} method="post" onSubmit={saveAlert}>
            <input type="hidden" name="device" value={id} />
            {service ? <input type="hidden" name="service" value={service} /> : null}
            <div className="col-md-12">
              <div className="form-group col-md-6">
                <div className="input-group selectContainer">
                  <span className="input-group-addon">Notify time</span>
                  <select name="alert_time" className="form-control" value={alertTime} onChange={(e) => setAlertTime(e.target.value)}>
                    <option value="">Select...</option>
                    {ALERT_MINUTES.map((m) => (
                      <option key={m} value={String(m)}>
                        {m} minutes
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-12">
              <button type="submit" className="btn btn-sm btn-primary saveAlertTime">
                Save
              </button>
            </div>
          </form>
        }
      </div>
    </div>
  )
}

function seedServices(page: DeviceWatchPage): ServiceDraft[] {
  const source =
    page.device_services.length > 0
      ? page.device_services
      : page.template_services.length > 0
        ? page.template_services
        : page.default_services.length > 0
          ? [
              {
                index: 0,
                service_id: '',
                service_name: '',
                monitored: 0,
                values: [{ field: 'Port', value: '' }],
              } satisfies WatchServiceRow,
            ]
          : [
              {
                index: 0,
                service_id: '',
                service_name: '',
                monitored: 0,
                values: [{ field: 'Port', value: '' }],
              },
            ]
  return source.map((row, i) => ({
    key: row.index || i + 1,
    service_id: row.service_id,
    monitored: row.monitored === 1,
    values: row.values.length > 0 ? row.values : [{ field: 'Port', value: '' }],
  }))
}

/**
 * PERECHILE. Ce camp apare depinde de masina - `virtual`, `tip`, cate
 * hipervizoare are firma - deci randurile nu se pot scrie unul cate unul in
 * markup: se face intai lista campurilor care au iesit, si abia apoi se taie
 * doua cate doua. Ordinea ramane a machetei vechi, neatinsa.
 */
function pairs(fields: ReactNode[]): ReactNode[] {
  const shown = fields.filter((field) => field !== null && field !== undefined && field !== false)
  const out: ReactNode[] = []
  for (let i = 0; i < shown.length; i += 2) {
    out.push(
      <FormRow key={i}>
        {shown[i]}
        {shown[i + 1] ?? null}
      </FormRow>,
    )
  }
  return out
}

/**
 * Cele sapte bifari, in ordinea din macheta. Al treilea camp e CARLIGUL de
 * plugin din portal, pastrat ca in `FormGrid.CheckField` - numele spune de unde
 * vine campul.
 *
 * `switchery` NU e printre ele, si asta e o deosebire cu buna stiinta fata de
 * carligele din fisa contului. Alea (`icheck-user`, `collaborator`) nu au
 * nicio regula in nicio foaie a portalului: sunt nume moarte, deci nu strica
 * nimic scrise pe o bifa. `switchery` ARE - `app.css:12568` ii da 50x30px,
 * colturi rotunde si fond alb, fiindca in portal clasa aia sta pe `<span>`-ul
 * DESENAT de plugin, nu pe casuta. In v4 pluginul nu ruleaza, deci clasa
 * ramanea pe casuta adevarata si o umfla intr-un dreptunghi alb gol de 50x30
 * in care nu se mai vedea daca e bifata sau nu - masurat in Chromium.
 * `gateway` si `hypervisor` raman: pe alea nu are nicio foaie nimic.
 */
const FLAGS: Array<[keyof WatchFlags, string, string | undefined]> = [
  ['autoclose', 'Autoclose', undefined],
  ['escalate', 'Escalate', undefined],
  ['gateway', 'Gateway', 'gateway'],
  ['hypervisor', 'Hypervisor', 'hypervisor'],
  ['no_keepalive', 'No keepalive', undefined],
  ['maintenance', 'Maintenance', undefined],
  ['development', 'Development', undefined],
]

function SelectField({
  label,
  id,
  name,
  value,
  onChange,
  required,
  children,
}: {
  label: string
  id: string
  name: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  children: ReactNode
}) {
  return (
    <HalfField label={label} required={required} htmlFor={id}>
      <div className={required ? 'selectContainer' : undefined}>
        <select id={id} name={name} className="form-control" required={required} value={value} onChange={(e) => onChange(e.target.value)}>
          {children}
        </select>
      </div>
    </HalfField>
  )
}

function MultiField({
  label,
  id,
  name,
  value,
  onChange,
  options,
  text,
  liveSearch,
}: {
  label: string
  id: string
  name: string
  value: string[]
  onChange: (v: string[]) => void
  options: WatchOption[]
  text: (row: WatchOption) => string
  liveSearch?: boolean
}) {
  return (
    <HalfField label={label} htmlFor={id}>
      <select
        id={id}
        name={name}
        className="form-control selectpicker"
        data-size="10"
        data-live-search={liveSearch ? 'true' : undefined}
        multiple
        value={value}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}
      >
        {options.map((row) => (
          <option key={row.ID} value={row.ID}>
            {text(row)}
          </option>
        ))}
      </select>
    </HalfField>
  )
}

/**
 * Cand lista din care s-ar alege e goala, campul nu se scoate din rand: ramane
 * pe locul lui, cu pastila rosie in locul casetei, ca sa nu se schimbe
 * perechile de la o masina la alta.
 */
function HintField({ label, text }: { label: string; text: string }) {
  return (
    <HalfField label={label}>
      <span className="label label-danger">{text}</span>
    </HalfField>
  )
}

function ServiceRow({
  row,
  list,
  onChange,
  onRemove,
  onMonitoring,
  onPickService,
}: {
  row: ServiceDraft
  list: { service_id: string; service_name: string }[]
  onChange: (row: ServiceDraft) => void
  onRemove: () => void
  onMonitoring: () => void
  onPickService: (serviceId: string) => void
}) {
  return (
    <div className="col-md-12 service" style={{ marginBottom: 10 }}>
      <div className="input-group col-md-3 pull-left">
        <span className="input-group-addon">Service</span>
        <select name={`service[${row.key}]`} className="form-control change-service" value={row.service_id} onChange={(e) => onPickService(e.target.value)}>
          <option value="">Select...</option>
          {list.map((service) => (
            <option key={service.service_id} value={service.service_id}>
              {service.service_name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4 pull-left change-values-template">
        {row.values.map((value) => (
          <div key={value.field} className="input-group col-xs-10" style={{ marginBottom: 5 }}>
            <span className="input-group-addon">{value.field}</span>
            <input
              className="form-control"
              type="text"
              value={value.value}
              name={`values[${row.key}][${value.field}]`}
              placeholder={value.placeholder ?? ''}
              onChange={(e) =>
                onChange({
                  ...row,
                  values: row.values.map((v) => (v.field === value.field ? { ...v, value: e.target.value } : v)),
                })
              }
            />
          </div>
        ))}
      </div>
      {/*
        Bifa purta `form-control`, iar Bootstrap ii da acolo `display: block` si
        `height: 34px` - masurat 13x34px in Chromium, adica un patratel de 13px
        agatat in capul unei cutii de trei ori mai inalte, cu eticheta cazuta
        dedesubt. Acum e `SettingsCheck`: bifa in desenul nou, cu "Active" in
        acelasi `<label>`. `check-monitored-service` ramane - e carligul de
        plugin din portal si nicio foaie nu are nimic pe el.
      */}
      <div className="col-md-2 monitored-service">
        <SettingsCheck
          id={`watch_monitored_${row.key}`}
          name={`monitored[${row.key}]`}
          className="check-monitored-service"
          value="1"
          checked={row.monitored}
          onChange={(on) => onChange({ ...row, monitored: on })}
          label="Active"
        />
      </div>
      <div className="col-md-2">
        {/* Numai pe un serviciu deja ales: intervalele se leaga de service_id. */}
        <button
          type="button"
          className="btn btn-sm btn-primary service-intervals"
          data-service={row.service_id || '0'}
          disabled={!row.service_id}
          onClick={onMonitoring}
        >
          <i className="fas fa-pencil-alt"></i> Monitoring settings
        </button>
      </div>
      <div className="col-md-1">
        <button type="button" className="btn btn-sm btn-danger remove-service" onClick={onRemove}>
          <i className="fa fa-trash"></i> Remove
        </button>
      </div>
    </div>
  )
}
